import React, { useEffect, useState } from 'react';
import { getAnalyticsFunnel, getFunnelEventsAnalytics } from '../../services/funnels';
import { logErrorToSentry } from '../../utils/handleApi';
import { Link, useParams } from 'react-router-dom';
import { AnalyticsFunnelPayload } from '../../services/interface';
import { decodeTokenService } from '../../services/decodeTokenService';
import { ChevronDown, DollarSign, Eye, ShoppingBag, Users } from 'lucide-react';
import { ArrowLeft } from 'lucide-react';
import StatCard from './StatCard';
import ProductStats from './ProductStats';
import PeriodSelect from './PeriodSelect';
import FunnelChart, { FunnelEventCount } from './FunnelChart';
import Loader from '../../components/common/Loader';
import { getStartDate, Period, PERIODS } from '../../utils/const';
import { formatCurrency, getCurrencySymbol } from '../../utils/formatters';
import { dictionary } from '../../utils/dictionary';
import { useAuth } from '../../context/AuthContext';

interface ProductAnalytics {
	conversion_rate: number;
	orders: number;
	product_id: string;
	revenue: number;
	type: 'PRINCIPAL' | 'UP_SELL' | 'BUMP_OFFER' | 'UNKNOWN';
	visits: number;
	avgTime?: string;
	visits_not_unique: number;
	units_sold: number;
}

interface FunnelMetrics {
	average_ticket: number;
	total_revenue: number;
	total_visits: number;
	total_visits_not_unique: number;
}

interface AnalyticsData {
	context: string;
	funnel_metrics: FunnelMetrics;
	product_analytics: ProductAnalytics[];
}
const AnalyticsPage: React.FC = () => {
	const { website_id } = useParams();
	const owner_id = decodeTokenService.getUserIdFromToken();
	const [date, setDate] = useState<Period>(Period.AllDates);
	const [stats, setStats] = useState<AnalyticsData | null>(null);
	const [funnelEvents, setFunnelEvents] = useState<FunnelEventCount[]>([]);
	const [showPeriodSelect, setShowPeriodSelect] = useState(false);
	const [activeTab, setActiveTab] = useState<'statistics' | 'chart'>('statistics');
	const { language, country } = useAuth();
	const currencySymbol = getCurrencySymbol(country);

	const filterValidProducts = (productAnalytics: ProductAnalytics[]) => {
		return productAnalytics.filter((product) => product.type !== "UNKNOWN");
	};

	const recalculateFunnelMetrics = (validProducts: ProductAnalytics[], originalMetrics: FunnelMetrics): FunnelMetrics => {
		const totalRevenue = validProducts.reduce((sum, product) => sum + product.revenue, 0);
		const totalOrders = validProducts.reduce((sum, product) => sum + product.orders, 0);
		
		return {
			total_revenue: totalRevenue,
			total_visits: originalMetrics.total_visits,
			average_ticket: totalOrders > 0 ? totalRevenue / totalOrders : 0,
			total_visits_not_unique: originalMetrics.total_visits_not_unique,
		};
	};

	const processAnalyticsData = (rawData: AnalyticsData): AnalyticsData => {
		const validProducts = filterValidProducts(rawData.product_analytics);
		const recalculatedMetrics = recalculateFunnelMetrics(validProducts, rawData.funnel_metrics);
		
		return {
			...rawData,
			product_analytics: validProducts,
			funnel_metrics: recalculatedMetrics
		};
	};


	useEffect(() => {
		if (!website_id || !owner_id) {
			return;
		}
		const period = PERIODS.find(p => p.key === date);
		const payload: AnalyticsFunnelPayload = {
			website_id: website_id,
			owner_id: owner_id,
			created_at: getStartDate(new Date(), period?.value || 0)
		};
		getAnalyticsFunnel(payload)
			.then((res) => {
				const processedStats = processAnalyticsData(res);
				setStats(processedStats);
			})
			.catch((err) => {
				console.error(err);
			});
		getFunnelEventsAnalytics(payload)
			.then((res) => {
				const events: FunnelEventCount[] = Object.entries(res).map(([event_type, count]) => ({
					event_type,
					count: count as number,
				}));
				setFunnelEvents(events);
			})
			.catch((err) => {
				logErrorToSentry(err);
			});
	}, [date]);


	if (!stats) {
		return (
			<div className="flex items-center justify-center h-screen">
				<Loader />
			</div>
		);
	}

	return (
		<div className="max-w-7xl mx-auto px-8 py-8">
			<div className="flex items-center justify-between mb-8">
				<div className="flex items-center gap-3">
					<Link to="/funnels" className="text-gray-400 hover:text-gray-600">
						<ArrowLeft size={24} />
					</Link>
					<h1 className="text-2xl font-bold text-gray-900">{dictionary["funnel-statistics"][language]}</h1>
				</div>

				<div className="relative flex items-center gap-3">
					{/* <div className="flex justify-end">
						<button className="bg-blue-500 text-white px-4 py-2 rounded-md" onClick={() => setIsDollar(!isDollar)}>{dictionary["change-currency"][language]}</button>
					</div> */}
					<button
						onClick={() => setShowPeriodSelect(!showPeriodSelect)}
						className="inline-flex items-center px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
					>
						{dictionary[date][language]}
						<ChevronDown size={16} className="ml-2" />
					</button>
					{showPeriodSelect && (
						<PeriodSelect
							selected={date}
							onSelect={(period) => {
								setDate(period as Period);
								setShowPeriodSelect(false);
								setStats(null);
							}}
							onClose={() => setShowPeriodSelect(false)}
						/>
					)}
				</div>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
				<StatCard
					title={dictionary["total-revenue"][language]}
					value={`${formatCurrency(stats.funnel_metrics.total_revenue, currencySymbol)}`}
					icon={DollarSign}
				/>
				<StatCard
					title={dictionary["total-visits"][language]}
					value={stats.funnel_metrics.total_visits_not_unique.toLocaleString()}
					icon={Eye}
				/>
				<StatCard
					title={dictionary["visits-unique"][language]}
					value={stats.funnel_metrics.total_visits.toLocaleString()}
					icon={Users}
				/>
				<StatCard
					title={dictionary["average-ticket"][language]}
					value={`${formatCurrency(stats.funnel_metrics.average_ticket, currencySymbol)}`}
					icon={ShoppingBag}
				/>
			</div>

			<div>
				{/* Tabs */}
				<div className="inline-flex h-10 items-center rounded-[10px] bg-gray-100 p-1 mb-6">
					<button
						onClick={() => setActiveTab('statistics')}
						className={`inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium rounded-[8px] transition-all ${
							activeTab === 'statistics'
								? 'bg-white text-gray-900 shadow-sm'
								: 'text-gray-500 hover:text-gray-700'
						}`}
					>
						{dictionary['statistics-tab']?.[language]}
					</button>
					<button
						onClick={() => setActiveTab('chart')}
						className={`inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium rounded-[8px] transition-all ${
							activeTab === 'chart'
								? 'bg-white text-gray-900 shadow-sm'
								: 'text-gray-500 hover:text-gray-700'
						}`}
					>
						{dictionary['chart-tab']?.[language]}
					</button>
				</div>

				{activeTab === 'statistics' && (
					<div className="space-y-6">
						<h2 className="text-xl font-semibold text-gray-900">{dictionary["funnel-products"][language]}</h2>
						<div className="space-y-4">
							{stats.product_analytics.length === 0 && (
								<h2 className="text-center text-gray-500 text-xl">{dictionary["no-sales-yet"][language]}</h2>
							)}
							{stats.product_analytics.map((product: ProductAnalytics) => (
								<ProductStats
									key={product.product_id}
									{...product}
									avgTime={product.avgTime || "0:00"}
								/>
							))}
						</div>
					</div>
				)}

				{activeTab === 'chart' && (
					<FunnelChart events={funnelEvents} />
				)}
			</div>
		</div>
	);
};

export default AnalyticsPage;
